import { useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { billingApi } from '@/lib/api/billing';
import { useAuth } from '@/contexts/AuthContext';
import { useEmbedStore } from '@/stores/embed-store';
import toast from 'react-hot-toast';

interface InsufficientBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Context for the action that triggered this modal */
  context: 'cloud' | 'publish';
}

export function InsufficientBalanceModal({
  isOpen,
  onClose,
  context,
}: InsufficientBalanceModalProps) {
  const { getIdToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleAddFunds = async () => {
    setIsLoading(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        setIsLoading(false);
        return;
      }

      // Create checkout session for adding funds (minimum $5)
      const response = await billingApi.addCloudFunds(token, 5);
      if (response.url) {
        // In embed mode, open in new tab (Stripe can't load in iframe)
        if (useEmbedStore.getState().isEmbedded) {
          window.open(response.url, '_blank');
          toast.success('Checkout opened in new tab');
          onClose();
          setIsLoading(false);
        } else {
          window.location.href = response.url;
        }
      }
    } catch (error: unknown) {
      console.error('Failed to add funds:', error);
      const message = error instanceof Error ? error.message : 'Failed to add funds';
      toast.error(message);
      setIsLoading(false);
    }
  };

  const description = context === 'cloud'
    ? 'Your cloud balance is too low to activate OptiDev Cloud. Please add funds to continue.'
    : 'Your cloud balance is too low to publish your app. Please add funds to continue.';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>
            <DialogTitle>Insufficient Balance</DialogTitle>
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="text-sm text-muted-foreground space-y-1 py-2">
          <p>Minimum top-up is $5.</p>
          <p>Unused balance never expires.</p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleAddFunds} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Redirecting...
              </>
            ) : (
              'Add Funds'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
